import React, {useRef} from 'react'
import {Box, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'

import {getGDIHideStyles, GDI_MEDIA_FILL_SX} from '../../utils/gdi-styles'
import {
    getExpandedFromContentArea,
    getAllExpandedFromContentArea
} from '../../utils/gdi-content-area'
import {mapAspectRatio} from '../../utils/gdi-aspect-ratio'
import {buildResponsive, hasValue, mapResponsiveEnum} from '../../utils/gdi-responsive'
import GDILinkBlock from '../../assets/gdiLinkBlock'
import GDITooltip from '../../common/gdiTooltip'
import {renderComponentsSwitch} from '../../../utils/contentRenderer'
import {useHeaderContext} from '../../../../../contexts/header-context'
import {useIsHoverReveal, useGDIGridColumns} from '../../utils/gdi-link-context'
import {useHeaderClearance} from '../../utils/use-header-clearance'

const VERTICAL_MAP = {
    Top: 'flex-start',
    Middle: 'center',
    Bottom: 'flex-end',
    'Space between': 'space-between'
}

const HORIZONTAL_MAP = {
    Left: 'flex-start',
    Center: 'center',
    Right: 'flex-end'
}

/* Grid equivalents for align-self / justify-self when the wrapper is `display: grid`
 * (overlay mode). Grid uses `start`/`end` rather than `flex-start`/`flex-end`. */
const GRID_VERTICAL_MAP = {
    Top: 'start',
    Middle: 'center',
    Bottom: 'end',
    'Space between': 'stretch'
}

const GRID_HORIZONTAL_MAP = {
    Left: 'start',
    Center: 'center',
    Right: 'end'
}

const TEXT_ALIGN_MAP = {
    Left: 'left',
    Center: 'center',
    Right: 'right'
}

/* Auto-margin pairs to position a sticky-column wrapper vertically inside its
 * parent — works in BOTH flex columns (where `align-self` is cross-axis only
 * and cannot move the child vertically) AND CSS grid cells. */
const STICKY_AUTO_MARGIN_MAP = {
    Top: {marginTop: 0, marginBottom: 'auto'},
    Middle: {marginTop: 'auto', marginBottom: 'auto'},
    Bottom: {marginTop: 'auto', marginBottom: 0},
    'Space between': {marginTop: 0, marginBottom: 0}
}

const buildStickyAutoMargin = (verticalRaw) => {
    const pick = (v) => STICKY_AUTO_MARGIN_MAP[v] || STICKY_AUTO_MARGIN_MAP.Top
    if (verticalRaw && typeof verticalRaw === 'object' && !Array.isArray(verticalRaw)) {
        const marginTop = {}
        const marginBottom = {}
        Object.entries(verticalRaw).forEach(([bp, v]) => {
            const m = pick(v)
            marginTop[bp] = m.marginTop
            marginBottom[bp] = m.marginBottom
        })
        return {marginTop, marginBottom}
    }
    const m = pick(verticalRaw)
    return {marginTop: m.marginTop, marginBottom: m.marginBottom}
}

const POSITION_MAP = {
    'Content overlays image': 'absolute',
    'Content above image': 'static',
    'Content below image': 'static'
}

const ORDER_MAP = {
    'Content above image': -1,
    'Content overlays image': 0,
    'Content below image': 0
}

const DIRECTION_MAP = {
    Stacked: 'column',
    'Side by Side': 'row'
}

const isAnyTrue = (v) =>
    v === true ||
    (typeof v === 'object' && v !== null && Object.values(v).some(Boolean))

/**
 * Group consecutive CTA blocks into a single render group, leaving other blocks
 * standalone. CTAs grouping (Side by Side vs Stacked) per the Content Tile spec
 * only applies "when more than one CTA is added in sequence in the layout"
 * — non-CTA children render in normal vertical flow.
 *
 * @param {Array} blocks - Slot children, each with a `__typename`
 * @returns {Array<{kind: 'cta', items: Array} | {kind: 'item', item: Object}>}
 */
const groupConsecutiveCtas = (blocks) => {
    const out = []
    let run = null
    for (const block of blocks) {
        if (block?.__typename === 'GDICtaBlock') {
            if (!run) {
                run = {kind: 'cta', items: []}
                out.push(run)
            }
            run.items.push(block)
        } else {
            run = null
            out.push({kind: 'item', item: block})
        }
    }
    return out
}

const getCtaGroupWidth = (items) => {
    /* Per-breakpoint width for the CTA group wrapper. Only `%` MinWidths
     * promote the group to the authored width — fixed px MinWidths are
     * a per-button concern, not a group concern.
     *
     * IMPORTANT: write an explicit `'auto'` at breakpoints that DID author
     * a non-% value (e.g. `212px` on desktop) so Chakra's mobile-first
     * inheritance does NOT bleed a mobile `100%` upward into them. Without
     * this, a stacked mobile group authored `100%` would leave the desktop
     * group at `100%` (inherited from md), stretching it to fill the
     * parent's fit-content width — which then keeps the row-flex buttons
     * left-anchored by `justify-content: flex-start` instead of letting
     * the outer Flex's `alignItems` honour `ContentHorizontalPosition`. */
    const width = {}
    let hasAuthoredPercent = false

    items.forEach((item) => {
        const minWidth = buildResponsive(
            item?.GDICtaBlockStylesPerViewType,
            (entry) => entry.MinWidth,
            undefined
        )

        const applyWidth = (bp, value) => {
            if (!hasValue(value)) return
            const normalized = typeof value === 'string' ? value.trim() : value
            if (typeof normalized === 'string' && normalized.endsWith('%')) {
                width[bp] = normalized
                hasAuthoredPercent = true
            } else if (
                width[bp] === undefined ||
                (typeof width[bp] === 'string' && !width[bp].endsWith('%'))
            ) {
                /* Block upward inheritance from a `%` at a lower bp, but
                 * never overwrite a `%` already set by a sibling CTA. */
                width[bp] = 'auto'
            }
        }

        if (minWidth && typeof minWidth === 'object' && !Array.isArray(minWidth)) {
            Object.entries(minWidth).forEach(([bp, value]) => applyWidth(bp, value))
            return
        }

        applyWidth('base', minWidth)
    })

    /* `width` may now contain `'auto'` placeholders for non-% breakpoints.
     * Only return it if at least one CTA actually authored a `%` MinWidth;
     * otherwise leave the group's width unset (default content-sized). */
    return hasAuthoredPercent ? width : undefined
}

const GDIContentTileBlock = ({
    HideBlockOn,
    HideOnDevices,
    MediaImageVideo,
    ContentSlot,
    GDIContentTileBlockLink,
    GDIContentTileBlockStylesPerViewType,
    TooltipContent,
    /* Set by ProductGrid when this tile renders as a PLP grid interruptor.
     * Forces the tile to ignore its authored ContentAspectRatio so its height
     * is driven by the SimpleGrid row (i.e. equal to the product tile's image
     * height) instead of imposing e.g. 19/28 on top of it. */
    isGridInterruptor = false
}) => {
    /* The site header is fixed at the top of the viewport, so sticky content needs an
     * offset equal to the header's measured height (kept in sync via the header context
     * + ResizeObserver). Falls back to 0 on SSR / before measurement. */
    const {headerHeight, pageType} = useHeaderContext()

    /* Ref on the outer tile wrapper so `useHeaderClearance` can measure where
     * this tile sits in the document. When the header is transparent (overlay)
     * and the tile renders behind it (e.g. a homepage hero), the tooltip control
     * — anchored at top: 24px inside the tile — would otherwise be hidden
     * underneath the header. The hook returns an effective top offset that
     * places the tooltip 24px below the header's visible bottom; returns `null`
     * when no clearance is needed so the authored default applies. */
    const tileRef = useRef(null)
    const tooltipClearancePx = useHeaderClearance(tileRef)
    /* PLP banners must keep the tooltip at its authored in-banner offset.
     * Applying header clearance on product-list pages makes the icon move
     * as the sticky header shows/hides during scroll. */
    const shouldApplyTooltipHeaderClearance = pageType !== 'product-list'
    const effectiveTooltipClearancePx = shouldApplyTooltipHeaderClearance
        ? tooltipClearancePx
        : null

    /* When inside a parent block authored with `DisplayTextAndCTAsOnHover` (e.g. the
     * carousel/grid), the content + gradient layers fade in only on hover. Touch
     * devices (no `hover: hover`) keep them visible always. */
    const isHoverReveal = useIsHoverReveal()

    /* Parent grid's per-breakpoint column count (from GDICarouselGridBlock). Used to
     * clamp this tile's authored ColSpan so it never exceeds the available tracks
     * — otherwise CSS Grid creates zero-width implicit columns and the layout breaks
     * (e.g. ColSpan=4 inside a 2-column mobile grid). */
    const parentGridColumns = useGDIGridColumns()

    /* All hooks above must run before any early return so hook order stays
     * stable across re-renders. */
    const hideStyles = getGDIHideStyles(HideBlockOn || HideOnDevices)
    if (hideStyles.doNotLoadComponent) return null

    const mediaBlock = getExpandedFromContentArea(MediaImageVideo)
    const slotBlocks = getAllExpandedFromContentArea(ContentSlot)
    const linkBlock = getExpandedFromContentArea(GDIContentTileBlockLink)
    const hasTooltip = getAllExpandedFromContentArea(TooltipContent).length > 0

    const stylesArr = GDIContentTileBlockStylesPerViewType

    /* Raw responsive enums — read once, then map into Chakra values via mapResponsiveEnum.
     * NOTE: CMS field names are `MediaContentDisplayMode` and `CTAsGroupingDisplay`. */
    const displayMode = buildResponsive(
        stylesArr,
        (e) => e.MediaContentDisplayMode,
        'Content overlays image'
    )
    const authoredOverlayMode = buildResponsive(
        stylesArr,
        (e) =>
            hasValue(e.MediaContentDisplayMode)
                ? e.MediaContentDisplayMode === 'Content overlays image'
                : null,
        false
    )
    const ctasGrouping = buildResponsive(stylesArr, (e) => e.CTAsGroupingDisplay, 'Side by Side')
    const verticalRaw = buildResponsive(stylesArr, (e) => e.ContentVerticalPosition, 'Middle')
    const horizontalRaw = buildResponsive(stylesArr, (e) => e.ContentHorizontalPosition, 'Center')

    /* Other responsive props passed through verbatim. */
    const stickyContent = buildResponsive(stylesArr, (e) => e.StickyContent === 'Yes', false)
    /* StickyColumn pins the entire tile (column) inside the parent grid container,
     * so a shorter column stays in view while the taller sibling scrolls past. */
    const stickyColumn = buildResponsive(stylesArr, (e) => e.StickyColumn === 'Yes', false)
    const transparentGradient = buildResponsive(stylesArr, (e) => e.TransparentGradientCss, null)
    const backgroundColor = buildResponsive(stylesArr, (e) => e.BackgroundColor, 'transparent')
    const aspectRatioRaw = buildResponsive(stylesArr, (e) => e.ContentAspectRatio, null)
    const aspectRatio = mapAspectRatio(aspectRatioRaw)
    const contentMaxWidth = buildResponsive(stylesArr, (e) => e.ContentMaxWidth, null)
    const paddings = buildResponsive(stylesArr, (e) => e.Paddings, undefined)
    const margins = buildResponsive(stylesArr, (e) => e.Margins, undefined)
    const colSpan = buildResponsive(stylesArr, (e) => Number(e.ColSpan) || null, null)

    const justify = mapResponsiveEnum(verticalRaw, VERTICAL_MAP)
    const alignItems = mapResponsiveEnum(horizontalRaw, HORIZONTAL_MAP)
    const textAlign = mapResponsiveEnum(horizontalRaw, TEXT_ALIGN_MAP)
    const contentPosition = mapResponsiveEnum(displayMode, POSITION_MAP)
    const contentOrder = mapResponsiveEnum(displayMode, ORDER_MAP)
    const contentDirection = mapResponsiveEnum(ctasGrouping, DIRECTION_MAP)
    /* Build clamped grid-column. For every breakpoint where the tile authors a ColSpan,
     * cap it to the parent's column count for that breakpoint (so e.g. ColSpan=4 inside
     * a 2-col mobile grid renders as `1 / -1` — full row — rather than overflowing). */
    const gridColumn = (() => {
        if (colSpan === null || colSpan === undefined) return undefined
        const colsAt = (bp) => {
            if (parentGridColumns && typeof parentGridColumns === 'object') {
                return Number(parentGridColumns[bp]) || null
            }
            return Number(parentGridColumns) || null
        }
        const buildSpan = (n, bp) => {
            const max = colsAt(bp)
            if (max && Number(n) >= max) return '1 / -1'
            return `span ${n}`
        }
        if (typeof colSpan === 'object') {
            return Object.fromEntries(
                Object.entries(colSpan).map(([bp, n]) => [bp, buildSpan(n, bp)])
            )
        }
        // scalar — assume base breakpoint
        return buildSpan(colSpan, 'base')
    })()
    const gridAlignSelf = mapResponsiveEnum(verticalRaw, GRID_VERTICAL_MAP)
    const gridJustifySelf = mapResponsiveEnum(horizontalRaw, GRID_HORIZONTAL_MAP)

    /* Build the gradient overlay element only when authored. Sits above the media but
     * below the content layer so text remains legible over busy imagery. */
    const gradientOverlay = transparentGradient ? (
        <Box
            data-gdi-overlay="gradient"
            position="absolute"
            inset={0}
            pointerEvents="none"
            background={transparentGradient}
            zIndex={1}
        />
    ) : null

    /* The Flex's `position` prop drives whether the content layer overlays the media
     * (absolute) or sits in flow (static). When absolute, it must fill the parent and
     * get inner padding so text isn't flush against the edges.
     *
     * `overlayBpFlags` carries the per-breakpoint truth so authors can flip the
     * layout per view type (e.g. "Content above image" on mobile +
     * "Content overlays image" on tablet/desktop). `isOverlayMode` stays as
     * the legacy "any breakpoint" boolean used for sx blocks that have no
     * sensible per-bp variant (hover-reveal, aspect-ratio decisions, etc.) —
     * those keep their existing behaviour while the props that actually
     * drive the layout (wrapper display, content position/width, wrapper
     * mx) read per-bp from `overlayBpFlags` below. */
    const hasOverlayPosition =
        contentPosition === 'absolute' ||
        (typeof contentPosition === 'object' &&
            Object.values(contentPosition).includes('absolute'))
    const isOverlayMode = Boolean(mediaBlock) && hasOverlayPosition
    const overlayBpFlags = (() => {
        if (!mediaBlock) return null
        const flagFor = (value) => value === 'absolute'
        if (typeof contentPosition === 'object' && contentPosition !== null) {
            return {
                base: flagFor(contentPosition.base),
                md: flagFor(contentPosition.md),
                lg: flagFor(contentPosition.lg)
            }
        }
        const flat = flagFor(contentPosition)
        return {base: flat, md: flat, lg: flat}
    })()
    const isMixedOverlayMode =
        !!overlayBpFlags &&
        new Set(Object.values(overlayBpFlags)).size > 1
    const hasAuthoredOverlayMode = isAnyTrue(authoredOverlayMode)
    /* Whether the tile EXPLICITLY authored a horizontal position (i.e. not left
     * as empty / "Default / Inherit"). We only let the tile override its child
     * text blocks' own `TextAlignment` when this is true — otherwise the text
     * blocks keep their authored alignment, which is the long-standing default. */
    const hasAuthoredHorizontalPosition = isAnyTrue(
        buildResponsive(
            stylesArr,
            (e) => (hasValue(e.ContentHorizontalPosition) ? true : null),
            false
        )
    )
    /* Text blocks normally stretch to 100% so their own `TextAlignment` resolves
     * against the full available width. Inside a Content Tile that's harmful in
     * two scenarios because it lets each child text block override the tile's
     * authored `ContentHorizontalPosition`:
     *   - overlay mode (content is a sized grid item placed by justifySelf)
     *   - text-only tile (no media) WITH an explicitly authored horizontal
     *     position: the tile's Flex owns horizontal placement via
     *     alignItems/textAlign; sibling CTAs honour that, so text blocks must
     *     too — otherwise text sticks to one edge while the CTAs sit
     *     centred (or vice versa). When the author left the position as
     *     Default / Inherit, we leave the text blocks alone so their own
     *     `TextAlignment` keeps working. */
    const disableTextAlignmentLayoutStretch =
        !mediaBlock && hasAuthoredHorizontalPosition
            ? true
            : hasAuthoredOverlayMode
            ? authoredOverlayMode
            : isOverlayMode
    const inFlowContentPosition =
        contentPosition && typeof contentPosition === 'object'
            ? Object.fromEntries(
                  Object.entries(contentPosition).map(([bp, value]) => [
                      bp,
                      value === 'absolute' ? 'static' : value
                  ])
              )
            : contentPosition === 'absolute'
            ? 'static'
            : contentPosition

    const isStickyOn = isAnyTrue(stickyContent)
    /* In no-media mode the wrapper is sized by its content, so the content's own
     * `position: sticky` has no scroll range to pin against. Promote
     * `StickyContent: Yes` to wrapper-level sticky — same effect as
     * `StickyColumn: Yes` — so the tile pins within its parent column while
     * sibling columns scroll past, keeping its authored in-flow position. */
    const isStickyColumn = isAnyTrue(stickyColumn) || (!mediaBlock && isStickyOn)

    /* In overlay mode we stack media and content via CSS Grid (single cell). The media
     * stretches to give the tile its height, while the content sizes to its own content
     * and uses align-self/justify-self for placement. This is what enables `position:
     * sticky` on the content to pin within the tile (green box) while the page scrolls. */

    /* `getGDIHideStyles` returns a per-breakpoint `display` object like
     * `{base: 'block', md: 'none', lg: 'block'}`. The wrapper needs
     * `display: flex` (above/below modes) or `display: grid` (overlay mode)
     * so children can be ordered or stacked. Hidden breakpoints stay 'none'.
     *
     * We emit raw `@media` rules into sx (not a Chakra responsive `display`
     * prop) so the hide rules sit in the same cascade layer as the other
     * authored sx rules — otherwise the Chakra responsive shorthand loses
     * to raw `@media` selectors already present in sx and the block stays
     * visible on hidden breakpoints. */
    const displayAt = (bp) => (overlayBpFlags && overlayBpFlags[bp] ? 'grid' : 'flex')
    const hideDisplay = hideStyles.display
    const wrapperDisplaySx =
        hideDisplay || isMixedOverlayMode
            ? {
                  display:
                      hideDisplay && hideDisplay.base === 'none' ? 'none' : displayAt('base'),
                  '@media screen and (min-width: 48em)': {
                      display:
                          hideDisplay && hideDisplay.md === 'none' ? 'none' : displayAt('md')
                  },
                  '@media screen and (min-width: 62em)': {
                      display:
                          hideDisplay && hideDisplay.lg === 'none' ? 'none' : displayAt('lg')
                  }
              }
            : {display: displayAt('base')}

    /* As a grid interruptor the tile height comes from the PLP row, so the media
     * has to fill and crop instead of rendering at its own natural ratio. */
    const gridInterruptorMediaSx = isGridInterruptor
        ? {...GDI_MEDIA_FILL_SX, height: '100%', flex: 1, minHeight: 0}
        : {}

    const hasAspectRatio =
        !isGridInterruptor &&
        (typeof aspectRatio === 'string' ||
            (aspectRatio &&
                typeof aspectRatio === 'object' &&
                Object.keys(aspectRatio).length > 0))

    /* When any CTA group inside the tile authors a `MinWidth: 100%` (typically
     * a stacked, full-width button), the wrapper CTA Flex gets `width: 100%`
     * (see render below). In overlay mode the content Flex is `fit-content` so
     * `justifySelf` can position it — but that makes the CTA's `%` resolve
     * against a button-sized parent and collapse to text width. To honour the
     * authored full-width CTA we widen the content Flex to 100% only at the
     * breakpoints where a CTA group requests 100% width; other breakpoints
     * keep `fit-content` and continue to be placed by `justifySelf`. */
    const ctaGroupFullWidthBps = (() => {
        if (!isOverlayMode) return null
        const bps = new Set()
        const groups = groupConsecutiveCtas(slotBlocks)
        groups.forEach((group) => {
            if (group.kind !== 'cta') return
            const w = getCtaGroupWidth(group.items)
            if (!w) return
            Object.entries(w).forEach(([bp, value]) => {
                if (value === '100%') bps.add(bp)
            })
        })
        return bps.size > 0 ? bps : null
    })()

    const overlayContentWidth = ctaGroupFullWidthBps
        ? {
              base: ctaGroupFullWidthBps.has('base') ? '100%' : 'fit-content',
              md: ctaGroupFullWidthBps.has('md') ? '100%' : 'fit-content',
              lg: ctaGroupFullWidthBps.has('lg') ? '100%' : 'fit-content'
          }
        : 'fit-content'

    /* Per-breakpoint resolution for the content layer's `position`, `width`
     * and `mx`. In single-mode tiles (every authored bp is overlay OR every
     * authored bp is non-overlay) this collapses to the same scalar / object
     * the legacy code emitted. In mixed mode (e.g. mobile = above-image,
     * desktop = overlay) it picks the correct value per bp so mobile flows
     * the content above the media while desktop continues to grid-stack it
     * over the media. */
    const overlayWidthAt = (bp) =>
        typeof overlayContentWidth === 'object' ? overlayContentWidth[bp] : overlayContentWidth
    const inFlowPositionAt = (bp) =>
        typeof inFlowContentPosition === 'object' && inFlowContentPosition !== null
            ? inFlowContentPosition[bp]
            : inFlowContentPosition
    const contentLayerPosition = overlayBpFlags
        ? isMixedOverlayMode
            ? {
                  base: overlayBpFlags.base
                      ? isStickyOn
                          ? 'sticky'
                          : 'relative'
                      : inFlowPositionAt('base'),
                  md: overlayBpFlags.md
                      ? isStickyOn
                          ? 'sticky'
                          : 'relative'
                      : inFlowPositionAt('md'),
                  lg: overlayBpFlags.lg
                      ? isStickyOn
                          ? 'sticky'
                          : 'relative'
                      : inFlowPositionAt('lg')
              }
            : isOverlayMode
            ? isStickyOn
                ? 'sticky'
                : 'relative'
            : inFlowContentPosition
        : inFlowContentPosition
    const contentLayerWidth = overlayBpFlags
        ? isMixedOverlayMode
            ? {
                  base: overlayBpFlags.base ? overlayWidthAt('base') : '100%',
                  md: overlayBpFlags.md ? overlayWidthAt('md') : '100%',
                  lg: overlayBpFlags.lg ? overlayWidthAt('lg') : '100%'
              }
            : isOverlayMode
            ? overlayContentWidth
            : '100%'
        : '100%'
    const contentLayerMx = contentMaxWidth
        ? isMixedOverlayMode
            ? {
                  base: overlayBpFlags.base ? undefined : 'auto',
                  md: overlayBpFlags.md ? undefined : 'auto',
                  lg: overlayBpFlags.lg ? undefined : 'auto'
              }
            : !isOverlayMode
            ? 'auto'
            : undefined
        : undefined

    const tile = (
        <Box
            ref={tileRef}
            data-block-type="GDIContentTileBlock"
            data-sticky-column={isStickyColumn ? 'true' : undefined}
            flexDirection="column"
            position={isStickyColumn ? 'sticky' : 'relative'}
            top={isStickyColumn ? `${headerHeight || 0}px` : undefined}
            /* Pair `top` with `bottom: 0` so the wrapper pins on BOTH scroll
             * directions inside the parent column — mirrors the with-media
             * content sticky setup. Without `bottom`, scrolling back up past
             * the pin point releases the wrapper and it no longer sticks. */
            bottom={isStickyColumn ? 0 : undefined}
            /* No explicit height: grid items default to `align-self: stretch`, so the
             * wrapper naturally fills the row when needed (giving sticky content scroll
             * room, and stretching text-only tiles next to image tiles). Setting
             * `height: 100%` here would conflict with `margin` (the margin would push
             * the box outside the cell), so we rely on grid stretch instead. */
            backgroundColor={backgroundColor}
            margin={margins}
            gridColumn={gridColumn}
            /* As a PLP grid interruptor we want the tile to stretch to the
             * SimpleGrid row (= product tile image height). The outer Box from
             * ProductGrid is the grid item and naturally stretches, but this
             * inner wrapper has no intrinsic height, so we have to fill it
             * explicitly. */
            height={isGridInterruptor ? '100%' : undefined}
            width={isGridInterruptor ? '100%' : undefined}
            sx={{
                ...wrapperDisplaySx,
                /* The carousel/grid parent applies `flex: 1` to children via a
                 * `& > *:not(...)` selector (specificity 0,2,1), which stretches
                 * the tile to the row height and overrides any plain `flex: ...`
                 * we set here (specificity 0,1,0). When an aspect ratio is
                 * authored we instead want the tile sized by `width × ratio`,
                 * so we use a self-targeting attribute selector to match the
                 * parent's specificity and reliably opt out of flex-grow. CSS
                 * aspect-ratio then computes height from the explicit width.
                 *
                 * Only applied at the wrapper level in overlay mode — in above/below
                 * modes the aspect ratio is applied to the media element only so the
                 * text can flow underneath/above without being clipped by `overflow:
                 * hidden`. */
                /* Apply the wrapper aspect ratio in two cases:
                 *  - overlay mode (single grid cell, content stacks on
                 *    media; the media stretches to fill the ratio)
                 *  - non-overlay mode WITHOUT media (text-only tile; the
                 *    media element that would normally carry the ratio
                 *    isn't rendered, so we apply it to the wrapper so the
                 *    text is laid out inside the authored ratio)
                 *
                 * Skip when non-overlay AND media is present — the media
                 * box already carries the ratio and the text can flow
                 * under/over without being clipped by `overflow: hidden`. */
                ...(hasAspectRatio && (isOverlayMode || !mediaBlock)
                    ? {
                          aspectRatio,
                          '&[data-block-type="GDIContentTileBlock"]': {
                              flex: 'none',
                              alignSelf: 'flex-start'
                          }
                      }
                    : {}),
                ...(isOverlayMode
                    ? {gridTemplateAreas: '"stack"', gridTemplateColumns: '1fr'}
                    : {}),
                /* Self-targeted selector raises specificity above the parent
                 * carousel/grid wrapper's `& > *` rule (which forces flex: 1 to
                 * stretch tiles to the row's height). For sticky-column to work,
                 * the tile must keep its natural height — otherwise it fills the
                 * containing block and has no room to pin.
                 *
                 * Vertical placement uses auto-margins from the authored
                 * `ContentVerticalPosition` (Top / Middle / Bottom). `align-self`
                 * cannot be used here because the parent slide wrapper is a flex
                 * column (see gdiCarouselGridBlock), where `align-self` controls
                 * the cross axis (horizontal). Auto-margins work in both flex
                 * columns and grid cells, so the pinned tile starts at the
                 * placed position before sticky engages. */
                ...(isStickyColumn
                    ? {
                          '&[data-sticky-column="true"]': {
                              flex: '0 0 auto',
                              height: 'auto',
                              width: '100%',
                              ...buildStickyAutoMargin(verticalRaw)
                          }
                      }
                    : {}),
                ...(hasAspectRatio && isOverlayMode ? {overflow: 'hidden'} : {}),
                ...(isHoverReveal
                    ? {
                          '@media (hover: hover) and (min-width: 62em)': {
                              '[data-gdi-content-layer="content"], [data-gdi-overlay="gradient"]': {
                                  opacity: 0,
                                  transition: 'opacity 0.25s ease'
                              },
                              '&:hover [data-gdi-content-layer="content"], &:hover [data-gdi-overlay="gradient"]': {
                                  opacity: 1
                              }
                          }
                      }
                    : {})
            }}
        >
            {/* Media layer. In overlay mode it occupies the single grid cell and lets the
             * image define the height. In above/below modes it's a flex item ordered
             * relative to the content. The gradient sits inside, absolutely positioned
             * to cover the image. */}
            {mediaBlock && (
                <Box
                    data-gdi-media-layer="media"
                    position="relative"
                    width="100%"
                    order={0}
                    sx={{
                        ...(isOverlayMode ? {gridArea: 'stack'} : {}),
                        ...gridInterruptorMediaSx,
                        /* In above/below modes the aspect ratio belongs on the media
                         * box, not the tile wrapper — otherwise the text content
                         * (rendered as a sibling) gets clipped by the wrapper's
                         * `overflow: hidden`. The inner image/video is forced to
                         * fill the box with `object-fit: cover` + `object-position:
                         * center` so it's cropped around its middle when its
                         * natural ratio differs from the authored container ratio
                         * (e.g. a tall image inside a 1/1 box). */
                        ...(hasAspectRatio && !isOverlayMode
                            ? {
                                  aspectRatio,
                                  overflow: 'hidden',
                                  '& img, & video, & picture': {
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      objectPosition: 'center',
                                      display: 'block'
                                  },
                                  /* GDIImageBlock wraps the picture in a Box that
                                   * defaults to maxWidth 100% — make sure that wrapper
                                   * also fills the aspect-ratio box vertically so the
                                   * image inside has a height to cover. */
                                  '& > [data-block-type="GDIImageBlock"], & > [data-block-type="GDIVideoBlock"]': {
                                      width: '100%',
                                      height: '100%'
                                  }
                              }
                            : {})
                    }}
                >
                    {renderComponentsSwitch(mediaBlock)}
                    {gradientOverlay}
                </Box>
            )}


            {/* Content layer — outer Flex stacks children vertically and handles overall
             * alignment. CTA grouping (Side by Side vs Stacked) ONLY applies to runs of
             * consecutive CTA blocks; non-CTA children render in normal flow.
             *
             * Overlay mode uses grid-cell stacking with align/justify-self instead of
             * absolute positioning so `position: sticky` (when authored) can pin the
             * content to the top of the viewport while the tile scrolls past. */}
            {slotBlocks.length > 0 && (
                <Flex
                    data-gdi-content-layer="content"
                    position={contentLayerPosition}
                    top={isStickyOn ? `${headerHeight || 0}px` : undefined}
                    bottom={isStickyOn ? 0 : undefined}
                    order={contentOrder}
                    direction="column"
                    justifyContent={justify}
                    alignItems={alignItems}
                    textAlign={textAlign}
                    /* In overlay mode the content layer is a CSS Grid item
                     * positioned via `justifySelf` (horizontal) and
                     * `alignSelf` (vertical). For those to actually move the
                     * box, it must NOT span the full track — otherwise
                     * `Right`/`Center` look identical to `Left`. So we shrink
                     * to content width here (still bounded by an authored
                     * `ContentMaxWidth` via `maxWidth` below) and let
                     * `justifySelf` place it. In above/below mode the
                     * content is in normal flow under/over the media and
                     * needs the full row width so `textAlign` /
                     * `alignItems` resolve over the visible area. */
                    width={contentLayerWidth}
                    maxHeight={hasAspectRatio ? '100%' : undefined}
                    overflow={hasAspectRatio ? 'hidden' : undefined}
                    /* Content height: when sticky we MUST keep natural height so the
                     * sticky element is shorter than its container and can pin while
                     * the wrapper scrolls past. In overlay mode the wrapper is a CSS
                     * Grid cell that stretches to fill the row by default — let the
                     * content size to its own contents so `alignSelf` (vertical
                     * position) can move it. Only stretch (height: 100%) in
                     * non-overlay mode without media only when an aspect ratio gives
                     * the wrapper a real height; otherwise 100% can resolve to 0 in
                     * text-only CLP content and collapse the tile. */
                    height={
                        !mediaBlock && !isStickyOn && !isOverlayMode && hasAspectRatio
                            ? '100%'
                            : 'auto'
                    }
                    /* In non-overlay, no-media mode the content Flex is the wrapper's
                     * only flow child. Without `flex: 1` it sits at its natural
                     * height at the top of a stretched wrapper (e.g. PLP grid
                     * interruptor row, or any parent giving the tile a height),
                     * which makes `justifyContent` (ContentVerticalPosition) a
                     * no-op. Using `flex: 1` instead of `height: 100%` keeps the
                     * CLP text-only safety (no defined wrapper height → nothing to
                     * distribute → no collapse), while letting the content fill any
                     * extra space so vertical placement actually moves children.
                     * When sticky-column is on (incl. the no-media promotion), the
                     * wrapper is short and pins as a whole — `flex: 1` would make
                     * it tall again and break the pin, so we skip it. */
                    flex={
                        !mediaBlock && !isOverlayMode && !isStickyColumn ? 1 : undefined
                    }
                    maxWidth={contentMaxWidth || undefined}
                    /* When the content layer is capped by an authored
                     * `ContentMaxWidth` and the tile is in non-overlay mode
                     * (the content layer is a normal block-flow child of the
                     * tile root, not a grid cell positioned via
                     * justify/alignSelf), center it horizontally inside the
                     * tile root so the capped content sits in the middle of
                     * the available width instead of left-anchored. Overlay
                     * mode already uses `justifySelf` (gridJustifySelf) for
                     * horizontal placement, so we don't touch it there. */
                    mx={contentLayerMx}
                    padding={paddings}
                    zIndex={2}
                    sx={
                        isOverlayMode
                            ? {
                                  gridArea: 'stack',
                                  /* Always honour authored vertical/horizontal
                                   * placement. Sticky needs a non-stretch
                                   * align so the box can be shorter than its
                                   * container; default to `start` if the
                                   * author left vertical position unset. */
                                  alignSelf: isStickyOn
                                      ? gridAlignSelf || 'start'
                                      : gridAlignSelf,
                                  justifySelf: gridJustifySelf
                              }
                            : undefined
                    }
                >
                    {groupConsecutiveCtas(slotBlocks).map((group, gIdx) => {
                        if (group.kind === 'cta') {
                            /* Apply an authored CTA MinWidth (%) to the group wrapper so
                             * the CTA actually stretches. Also runs in overlay mode now
                             * — the content Flex's `width` (set above) is widened to
                             * 100% at the matching breakpoints so the % resolves
                             * against the tile rather than against a `fit-content`
                             * parent (which would collapse the CTA to its text). */
                            const ctaGroupWidth = getCtaGroupWidth(group.items)

                            return (
                                <Flex
                                    key={`gdi-ct-cta-${gIdx}`}
                                    data-gdi-cta-group="true"
                                    direction={contentDirection}
                                    flexWrap="wrap"
                                    gap="8px"
                                    width={ctaGroupWidth}
                                >
                                    {group.items.map((child, idx) => (
                                        <React.Fragment
                                            key={child?.ContentLink?.GuidValue || `gdi-ct-cta-${gIdx}-${idx}`}
                                        >
                                            {renderComponentsSwitch(child)}
                                        </React.Fragment>
                                    ))}
                                </Flex>
                            )
                        }
                        return (
                            <React.Fragment
                                key={group.item?.ContentLink?.GuidValue || `gdi-ct-${gIdx}`}
                            >
                                {renderComponentsSwitch(
                                    group.item,
                                    group.item?.__typename === 'GDITextBlock'
                                        ? {disableTextAlignmentLayoutStretch}
                                        : undefined
                                )}
                            </React.Fragment>
                        )
                    })}
                </Flex>
            )}

            {/* Tooltip control — sits above the content layer (zIndex: 3) in
             * the top-left of the tile. The trigger handler stops propagation
             * so clicks on the tooltip don't follow the (optional)
             * GDILinkBlock wrapping this tile. Renders nothing when the
             * author hasn't provided any TooltipContent.
             *
             * When the tile renders behind a transparent header (e.g. a
             * homepage hero), `useHeaderClearance` returns an offset that
             * lands the control 24px under the header's visible bottom so
             * it isn't hidden by the overlay header. */}
             {hasTooltip && (
                <Box
                    data-gdi-tile-tooltip="true"
                    position="absolute"
                    top={
                        effectiveTooltipClearancePx != null
                            ? `${effectiveTooltipClearancePx}px`
                            : {base: '16px', lg: '24px'}
                    }
                    left={{base: '16px', lg: '24px'}}
                    zIndex={3}
                >
                    <GDITooltip tooltipContent={TooltipContent} placement="right" />
                </Box>
            )}
        </Box>
    )

    if (linkBlock) {
        /* `data-gdi-tile-link` lands on the outer anchor so descendant CTAs
         * styled as text-link / Mega Menu Link can mirror their `:hover`
         * styles when the wrapping link is hovered — see
         * `getParentTileLinkHoverSx` and `getMegaMenuLinkSx` in
         * gdi-cta-style.js. */
        return (
            <GDILinkBlock
                {...linkBlock}
                linkProps={{'data-gdi-tile-link': 'true'}}
            >
                {tile}
            </GDILinkBlock>
        )
    }

    return tile
}

GDIContentTileBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    HideOnDevices: PropTypes.string,
    MediaImageVideo: PropTypes.array,
    ContentSlot: PropTypes.array,
    GDIContentTileBlockLink: PropTypes.array,
    GDIContentTileBlockStylesPerViewType: PropTypes.array,
    TooltipContent: PropTypes.array,
    GDIContentTileBlockAudienceTargeting: PropTypes.object,
    isGridInterruptor: PropTypes.bool
}

export default GDIContentTileBlock
